import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Building,
  User,
  Upload,
  Download,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminDocentiPage = () => {
  const [docenti, setDocenti] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docenteToDelete, setDocenteToDelete] = useState(null);
  const [editingDocente, setEditingDocente] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    specialization: "",
    bio: "",
    photo_url: "",
    association_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docentiRes, assocRes] = await Promise.all([
        axios.get(`${API}/docenti`),
        axios.get(`${API}/associations`),
      ]);
      setDocenti(docentiRes.data);
      setAssociations(assocRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value === "none" ? "" : value });
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Use the backend URL with the API path
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setFormData({ ...formData, photo_url: fullUrl });
      toast.success("Foto caricata con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      specialization: "",
      bio: "",
      photo_url: "",
      association_id: "",
    });
    setEditingDocente(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (docente) => {
    setEditingDocente(docente);
    setFormData({
      name: docente.name,
      email: docente.email,
      phone: docente.phone,
      specialization: docente.specialization || "",
      bio: docente.bio || "",
      photo_url: docente.photo_url || "",
      association_id: docente.association_id || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const docenteData = {
      ...formData,
      association_id: formData.association_id || null,
    };

    try {
      if (editingDocente) {
        await axios.put(`${API}/admin/docenti/${editingDocente.docente_id}`, docenteData);
        toast.success("Docente aggiornato con successo");
      } else {
        await axios.post(`${API}/admin/docenti`, docenteData);
        toast.success("Docente creato con successo");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!docenteToDelete) return;

    try {
      await axios.delete(`${API}/admin/docenti/${docenteToDelete.docente_id}`);
      toast.success("Docente eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setDocenteToDelete(null);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/docenti/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'docenti.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Gestione Docenti
            </h1>
            <p className="text-muted-foreground">
              Aggiungi e modifica i docenti dell'academy
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="rounded-full"
              disabled={exporting}
              data-testid="export-docenti-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Export..." : "Export CSV"}
            </Button>
            <Button
              onClick={openCreateDialog}
              className="rounded-full"
              data-testid="create-docente-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Docente
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : docenti.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {docenti.map((docente) => (
              <Card key={docente.docente_id} className="rounded-2xl border-border/50 card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <Avatar className="w-16 h-16 border-2 border-primary/20">
                      <AvatarImage src={docente.photo_url} alt={docente.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-serif text-lg">
                        {docente.name.split(" ").map((n) => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-lg font-medium truncate">{docente.name}</h3>
                      {docente.specialization && (
                        <p className="text-sm text-primary truncate">{docente.specialization}</p>
                      )}
                      {docente.association_name && (
                        <div className="flex items-center gap-1 mt-1">
                          <Building className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground truncate">
                            {docente.association_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{docente.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{docente.phone}</span>
                    </div>
                  </div>

                  {docente.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {docente.bio}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-full"
                      onClick={() => openEditDialog(docente)}
                      data-testid={`edit-docente-${docente.docente_id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Modifica
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        setDocenteToDelete(docente);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-docente-${docente.docente_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Nessun docente registrato</p>
              <Button onClick={openCreateDialog} className="rounded-full">
                Aggiungi il primo docente
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingDocente ? "Modifica Docente" : "Nuovo Docente"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Dott.ssa Maria Rossi"
                className="rounded-xl"
                data-testid="docente-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="email@esempio.it"
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+39 333 1234567"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="specialization">Specializzazione</Label>
              <Input
                id="specialization"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                placeholder="es. Estetica Oncologica Avanzata"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="association_id">Associazione</Label>
              <Select
                value={formData.association_id || "none"}
                onValueChange={(v) => handleSelectChange("association_id", v)}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona associazione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna associazione</SelectItem>
                  {associations.map((assoc) => (
                    <SelectItem key={assoc.association_id} value={assoc.association_id}>
                      {assoc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Foto Docente</Label>
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                  {formData.photo_url ? (
                    <img src={formData.photo_url} alt="Foto" className="w-full h-full object-cover object-center" />
                  ) : (
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Caricamento..." : "Carica Foto"}
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    <p><strong>Formati:</strong> JPG, PNG, GIF, WEBP</p>
                    <p><strong>Consigliato:</strong> 400x400px (quadrata)</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Oppure inserisci URL:</p>
                  <Input
                    id="photo_url"
                    name="photo_url"
                    type="url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Biografia</Label>
              <Textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={3}
                placeholder="Breve descrizione del docente..."
                className="rounded-xl"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={saving}
                data-testid="save-docente-btn"
              >
                {saving ? "Salvataggio..." : editingDocente ? "Salva Modifiche" : "Crea Docente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina docente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{docenteToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminDocentiPage;
